<template>
  <div class="mb-4">
    <SoTabs class="pb-4" divider>
      <SoTab
        v-for="(meterpoint, index) in selectedFuels"
        :key="meterpoint.id"
        :ref="`selected-meter-${meterpoint.id}`"
        :title="`${meterpoint.isElectricity ? 'Electricity' : 'Gas'} (${
          meterpoint.meterpointType
        } ${meterpoint.identifier})`"
        :icon="meterpoint.isElectricity ? 'electric' : 'gas'"
        :icon-color="meterpoint.isElectricity ? 'electric' : 'gas'"
        class="pa-1"
        eager
      >
        <p>Meter Serial Number ({{ meterpoint.meters[0].identifier }})</p>

        <SoAlert
          v-if="reviewPayment.showBasicEstimationFor(meterpoint)"
          type="info"
          class="my-4 w-50"
        >
          If the usage is likely to change, use the Basic Estimation.
        </SoAlert>

        <SoRadioCards
          v-model="reviewPayment.selectedEstimates[index]"
          class="mt-8"
        >
          <SoRadioCard
            v-if="reviewPayment.showBasicEstimationFor(meterpoint)"
            :value="EstimationChoice.Basic"
            max-width="33%"
          >
            <template #title>Basic Estimation</template>
            <MeterpointUsageCostHeader
              v-if="basicUsageAmendedMeterpoints[index]"
              :meterpoint="basicUsageAmendedMeterpoints[index]"
            />
            <UsageEstimator
              :is-electricity="meterpoint.isElectricity"
              :is-two-rate="meterpoint.hasTwoRateMeter"
              @updated="($event) => updateBasicUsage(meterpoint, $event, index)"
            />
          </SoRadioCard>

          <SoRadioCard :value="EstimationChoice.Industry" max-width="33%">
            <template #title>Industry - Current value</template>
            <MeterpointUsageCostHeader :meterpoint="meterpoint" />
            <div>EAC {{ meterpoint.totalUsage }} kWh</div>
          </SoRadioCard>

          <SoRadioCard :value="EstimationChoice.Reading" max-width="33%">
            <template #title>Meter Reading Estimation</template>
            <MeterpointUsageCostHeader
              v-if="industryMeterpoints[index]"
              :meterpoint="
                readUsageAmendedMeterpoints[index] ?? industryMeterpoints[index]
              "
              :hide="!readUsageAmendedMeterpoints[index]"
            />
            <UsageReadCalculator
              v-for="register in meterpoint.registers"
              :key="`${register.id}-${index}-${version}`"
              :meterpoint="meterpoint"
              :register="register"
              required
              @updated="($event) => updateReadUsage(register, $event, index)"
            />
          </SoRadioCard>
        </SoRadioCards>
      </SoTab>
    </SoTabs>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useReviewPaymentStore } from "@/stores/reviewPayment"
  import { EstimationChoice } from "@/types/ReviewPayment"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { IUsageEstimation } from "@/services/UsageEstimationService/types"
  import { IRegister } from "@/models/meterRegister/meterRegister.types"
  import SoTabs from "@/components/tabs/SoTabs.vue"
  import SoTab from "@/components/tabs/SoTab.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoRadioCard from "@/components/SoRadioCard.vue"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import UsageEstimator from "@/views/customer/components/UsageEstimator/UsageEstimator.vue"
  import UsageReadCalculator from "@/views/customer/components/UsageReadCalculator.vue"
  import MeterpointUsageCostHeader from "./MeterpointUsageCostHeader.vue"

  export default defineComponent({
    components: {
      SoTabs,
      SoTab,
      SoAlert,
      SoRadioCard,
      SoRadioCards,
      UsageEstimator,
      UsageReadCalculator,
      MeterpointUsageCostHeader,
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        reviewPayment: useReviewPaymentStore(),
        basicUsageAmendedMeterpoints: [] as IMeterpoint[],
        readUsageAmendedMeterpoints: [] as IMeterpoint[] | null[],
        industryMeterpoints: [] as IMeterpoint[],
        EstimationChoice,
        version: 0,
      }
    },
    computed: {
      selectedFuels() {
        return this.reviewPayment.selectedFuels
      },
      selectedUsageEstimatedMeterpoint() {
        return this.reviewPayment.selectedEstimates.map((estimation, index) => {
          if (estimation === EstimationChoice.Basic) {
            return this.basicUsageAmendedMeterpoints[index]
          } else if (estimation === EstimationChoice.Reading) {
            return this.readUsageAmendedMeterpoints[index]
          } else if (estimation === EstimationChoice.Industry) {
            return this.industryMeterpoints[index]
          } else {
            return null
          }
        })
      },
    },
    watch: {
      selectedUsageEstimatedMeterpoint: {
        deep: true,
        handler(val) {
          this.reviewPayment.usageEstimatedMeterpoints = val
        },
      },
      selectedFuels() {
        this.initData()
        this.version += 1
      },
    },
    mounted() {
      this.initData()
      this.current.isSidePanelCollapsed = true
    },
    methods: {
      initData() {
        this.industryMeterpoints = [...this.selectedFuels]
        this.basicUsageAmendedMeterpoints = [...this.selectedFuels]
        this.readUsageAmendedMeterpoints = new Array(
          this.selectedFuels.length,
        ).fill(null)
      },
      updateBasicUsage(
        meterpoint: IMeterpoint,
        newUsage: IUsageEstimation,
        index: number,
      ) {
        this.basicUsageAmendedMeterpoints[index] =
          this.basicUsageAmendedMeterpoints[index].amendUsage(
            meterpoint.registers.reduce((usageRecord, register) => {
              usageRecord[register.id] = register.rateName
                ? register.rateName === "Standard"
                  ? newUsage.electricity
                  : register.rateName === "Day"
                    ? newUsage.electricityDay
                    : newUsage.electricityNight
                : newUsage.gas
              return usageRecord
            }, {}),
          )
      },
      updateReadUsage(register: IRegister, newUsage: number, index: number) {
        this.readUsageAmendedMeterpoints[index] = this.industryMeterpoints[
          index
        ].amendUsage({
          [register.id]: newUsage,
        })
      },
    },
  })
</script>
